import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Globe, Loader2, CheckCircle, Languages, ArrowRight, Volume2 } from 'lucide-react';
import { toast } from 'sonner';
import { getTier1Languages } from '../lib/languages';
import GoogleSignInButton from '../components/GoogleSignInButton';

export default function SignupPage() {
  const { t, language: currentLanguage, setLanguage: setAppLanguage } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('individual');
  const [signupIntent, setSignupIntent] = useState('buyer'); // V4: 'buyer' | 'supplier' | 'service' | 'team'
  const [selectedLanguage, setSelectedLanguage] = useState(currentLanguage);
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showLanguageConfirmation, setShowLanguageConfirmation] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const tier1Languages = getTier1Languages();

  const handleLanguageChange = (langCode) => {
    setSelectedLanguage(langCode);
    setAppLanguage(langCode); // Update UI language immediately
  };

  // Get the selected language info for the confirmation dialog
  const selectedLangInfo = tier1Languages.find(l => l.code === selectedLanguage);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.error(t('error.validation'));
      return;
    }

    const formData = {
      name,
      email,
      password,
      role,
      preferred_language: selectedLanguage,
      accepted_terms: acceptedTerms,
      signup_intent: signupIntent
    };

    // Skip confirmation for English — just submit directly
    if (selectedLanguage === 'en') {
      setPendingFormData(formData);
      setLoading(true);
      try {
        await signup(formData);
        toast.success(t('success.created'));
        navigate("/onboarding/welcome");
      } catch (error) {
        toast.error(error.message || t('error.generic'));
      } finally {
        setLoading(false);
      }
      return;
    }

    // Non-English: confirm language choice
    setPendingFormData(formData);
    setShowLanguageConfirmation(true);
  };

  const confirmAndSubmit = async () => {
    if (!pendingFormData) return;

    setLoading(true);
    setShowLanguageConfirmation(false);

    try {
      await signup(pendingFormData);
      toast.success(t('success.created'));
      if (pendingFormData.role === 'individual') {
        navigate('/welcome');
      } else {
        navigate("/onboarding/welcome");
      }
    } catch (error) {
      toast.error(error.message || t('error.generic'));
    } finally {
      setLoading(false);
      setPendingFormData(null);
    }
  };

  const changeLanguageAndReturn = () => {
    setShowLanguageConfirmation(false);
    // Focus will return to form, user can change language
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center mb-8">
          <img src="/assets/pocketfiler-header-logo.png" alt="PocketFiler" className="h-12" />
        </div>

        <Card>
          <CardHeader className="text-center">
            <CardTitle>{t('auth.signup.title')}</CardTitle>
            <CardDescription>{t('auth.signup.subtitle')}</CardDescription>
          </CardHeader>
          
          <div className="mb-4">
            <GoogleSignInButton mode="signup" />
          </div>
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">or signup with email</span>
            </div>
          </div>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t('auth.signup.name')}</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="signup-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.signup.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="signup-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t('auth.signup.password')}</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="signup-password-input"
                />
                {password.length > 0 && (
                  <div className="mt-2 space-y-1 text-xs" data-testid="password-requirements">
                    {[
                      { label: 'At least 8 characters', valid: password.length >= 8 },
                      { label: 'One uppercase letter', valid: /[A-Z]/.test(password) },
                      { label: 'One lowercase letter', valid: /[a-z]/.test(password) },
                      { label: 'One number', valid: /[0-9]/.test(password) },
                      { label: 'One special character', valid: /[!@#$%^&*()_+\-=\[\]{};:\'",.<>?/|`~]/.test(password) },
                    ].map((req, i) => (
                      <div key={i} className={`flex items-center gap-1.5 ${req.valid ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`}>
                        <span className={`inline-flex h-3.5 w-3.5 rounded-full items-center justify-center text-[9px] font-bold ${req.valid ? 'bg-green-500 text-white' : 'bg-muted border border-muted-foreground/30'}`}>
                          {req.valid ? '✓' : ''}
                        </span>
                        <span>{req.label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="role">{t('auth.signup.accountType')}</Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger data-testid="signup-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Personal Use</SelectItem>
                      <SelectItem value="business">Business / Professional</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="language">{t('settings.profile.language')}</Label>
                  <Select value={selectedLanguage} onValueChange={handleLanguageChange}>
                    <SelectTrigger data-testid="language-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {tier1Languages.map((lang) => (
                        <SelectItem key={lang.code} value={lang.code}>
                          {lang.nativeName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Terms & Privacy Checkbox */}
              <div className="flex items-start gap-2 pt-2">
                <input
                  type="checkbox"
                  id="acceptTerms"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  data-testid="accept-terms-checkbox"
                />
                <label htmlFor="acceptTerms" className="text-sm text-muted-foreground leading-tight">
                  I agree to the{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    Terms of Service
                  </a>
                  {' '}and{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    Privacy Policy
                  </a>
                </label>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col gap-4">
              <Button
                type="submit"
                className="w-full"
                disabled={loading || !acceptedTerms}
                data-testid="signup-form-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common.loading')}
                  </>
                ) : (
                  t('auth.signup.submit')
                )}
              </Button>
              <p className="text-sm text-muted-foreground text-center">
                {t('auth.signup.hasAccount')}{' '}
                <Link to="/login" className="text-primary hover:underline">
                  {t('auth.signup.loginLink')}
                </Link>
              </p>
            </CardFooter>
          </form>
        </Card>
      </div>

      {/* Language Confirmation Dialog */}
      <Dialog open={showLanguageConfirmation} onOpenChange={setShowLanguageConfirmation}>
        <DialogContent className="sm:max-w-md" data-testid="language-confirmation-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Languages className="h-5 w-5 text-primary" />
              {t('language.confirm.title')}
            </DialogTitle>
            <DialogDescription>
              {t('language.confirm.subtitle')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Selected Language Display */}
            <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Globe className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-lg">{selectedLangInfo?.nativeName}</p>
                    <p className="text-sm text-muted-foreground">{selectedLangInfo?.name}</p>
                  </div>
                </div>
                <CheckCircle className="h-6 w-6 text-green-500" />
              </div>
            </div>

            {/* Explanation */}
            <Alert className="bg-muted/50">
              <AlertDescription className="text-sm">
                {t('language.picker.confirmation', { language: selectedLangInfo?.nativeName || selectedLanguage })}
              </AlertDescription>
            </Alert>

            {/* Regional Variant Notice for Chinese */}
            {(selectedLanguage === 'zh-CN' || selectedLanguage === 'zh') && (
              <Alert className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
                <AlertDescription className="text-sm text-amber-800 dark:text-amber-200">
                  {t('language.confirm.chineseNote') || 'Note: You selected Simplified Chinese (简体中文). If you prefer Traditional Chinese, please change your selection.'}
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={changeLanguageAndReturn}
              className="w-full sm:w-auto"
            >
              {t('language.confirm.change') || 'Change Language'}
            </Button>
            <Button
              onClick={confirmAndSubmit}
              disabled={loading}
              className="w-full sm:w-auto"
              data-testid="confirm-language-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.loading')}
                </>
              ) : (
                <>
                  <ArrowRight className="mr-2 h-4 w-4" />
                  {t('language.confirm.continue') || 'Continue with'} {selectedLangInfo?.nativeName}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
